"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRetailLocationAvailableForOrderType = exports.useCanPlaceOrder = exports.useOrderWithinOperatingHours = exports.useRequestedReadyTimeLabel = exports.useRetailLocationMaximumOrderValueCheck = exports.useRetailLocationAvailability = void 0;
const react_1 = require("react");
const moment = require("moment-timezone");
const SalesOrderContext_1 = require("../contexts/SalesOrderContext");
const time_1 = require("../../../../helpers/time");
const currentUnixTimestamp_1 = require("../../../../apis/spg/util/currentUnixTimestamp");
const useRequiredContext_1 = require("../../../../hooks/useRequiredContext");
const OrderTypeContext_1 = require("../contexts/OrderTypeContext");
const useRetailLocationDeliveryDetails_1 = require("./useRetailLocationDeliveryDetails");
const TSalesOrderType_1 = require("../types/TSalesOrderType");
const useTickReRender_1 = require("../../../../hooks/useTickReRender");
const RetailLocationsContext_1 = require("../contexts/RetailLocationsContext");
const constants_1 = require("../../../../helpers/constants");
/**
 * Helper hook to determine retail location states that updates over time.
 */
const useRetailLocationAvailability = (retailLocation) => {
    const tick = (0, useTickReRender_1.useTickReRender)();
    const { retailLocations } = (0, useRequiredContext_1.useRequiredContext)(RetailLocationsContext_1.RetailLocationsContext);
    const contextRetailLocation = (0, react_1.useMemo)(() => {
        if (!retailLocation) {
            return null;
        }
        if (!Array.isArray(retailLocations)) {
            return null;
        }
        return retailLocations.find(rl => rl.retailLocationId === retailLocation.retailLocationId);
    }, [retailLocation, retailLocations]);
    // determines whether the location is operating and the current time is less than the closing time
    // operating could also mean that the store isn't open yet for the current time but will be later
    const isOperating = (0, react_1.useMemo)(() => {
        // location is not operating if it doesn't exist
        if (!contextRetailLocation) {
            return false;
        }
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const { closingTime, isOpenToday } = contextRetailLocation.getOperatingDetailsForTime(timestampNow);
        if (!isOpenToday)
            return false;
        return timestampNow <= closingTime;
    }, [contextRetailLocation, tick]);
    const canPickup = (0, exports.useRetailLocationAvailableForOrderType)(contextRetailLocation, TSalesOrderType_1.TSalesOrderType.Pickup);
    const canDeliver = (0, exports.useRetailLocationAvailableForOrderType)(contextRetailLocation, TSalesOrderType_1.TSalesOrderType.Delivery);
    const retailLocationAvailableForOrdering = (0, react_1.useMemo)(() => {
        if (!contextRetailLocation) {
            return false;
        }
        if (!isOperating) {
            return false;
        }
        return canPickup || canDeliver;
    }, [contextRetailLocation, canPickup, canDeliver, isOperating, tick]);
    // determines the minimum and maximum times (as moment objects) that an order can be placed
    const allowedOrderTimes = (0, react_1.useMemo)(() => {
        var _a;
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        // cannot have times to place order if there is none
        if (!contextRetailLocation) {
            const timeZoneId = ((_a = contextRetailLocation === null || contextRetailLocation === void 0 ? void 0 : contextRetailLocation.timezone) === null || _a === void 0 ? void 0 : _a.timeZoneId) || moment.tz.guess();
            return {
                minTime: timestampNow,
                maxTime: moment()
                    .tz(timeZoneId)
                    .endOf("d")
                    .unix()
            };
        }
        const { openingTime, closingTime: maxTime, isOpenToday } = contextRetailLocation.getOperatingDetailsForTime(timestampNow);
        if (!isOpenToday) {
            return {
                minTime: timestampNow,
                maxTime: timestampNow
            };
        }
        // if the current time is before the opening then the minimum is that
        // otherwise it will be the current time up until the closing
        const minTime = timestampNow < openingTime
            ? openingTime
            : timestampNow <= maxTime
                ? timestampNow
                : maxTime;
        return {
            minTime,
            maxTime // maximum will always be closing
        };
    }, [contextRetailLocation, tick]);
    // shows current operating state of store for given time
    const operatingStatusLabel = (0, react_1.useMemo)(() => {
        if (!contextRetailLocation) {
            return "";
        }
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const { isOpen, openingHours, timezone: { timeZoneId } } = contextRetailLocation;
        if (!canDeliver && !canPickup) {
            // store has online ordering disabled, show it as offline
            return "Store is unavailable for online ordering";
        }
        const timeFormat = "h:mmA"; // e.g. "11:00am"
        const dayFormat = "dddd"; // e.g "Monday"
        const { openingTime, closingTime } = contextRetailLocation.getOperatingDetailsForTime(timestampNow);
        const momentOpeningTime = moment.unix(openingTime).tz(timeZoneId);
        const momentClosingTime = moment.unix(closingTime).tz(timeZoneId);
        const openingTimeFormatted = momentOpeningTime.format(timeFormat);
        const closingTimeFormatted = momentClosingTime.format(timeFormat);
        const openPastMidnight = !momentClosingTime.isSame(momentOpeningTime, "day");
        if (isOpen) {
            // store is open right Now, compute status label based on its next closing time
            return openPastMidnight
                ? `store open - closes tomorrow ${closingTimeFormatted}`
                : `store open - closes at ${closingTimeFormatted}`;
        }
        if (timestampNow < openingTime) {
            // although closed right Now store is going to open today
            return `store closed - opens at ${openingTimeFormatted}`;
        }
        if (timestampNow >= closingTime) {
            // store is closed now, compute status label based on its next opening hour
            // get current moment day for current time
            const momentToday = moment.unix(timestampNow).tz(timeZoneId);
            // whether the current time is on the same day as the store's current closing time
            const isSameDayAsClosingTime = momentToday.isSame(momentClosingTime, "day");
            // whether the current time is on another time other than the opening time
            // a. now and closing are on the same day and closing time is on another day to opening
            // b. now and closing times are on different days and time now is on day of opening
            const isOtherDayFromOpeningTime = (isSameDayAsClosingTime && openPastMidnight) ||
                (!isSameDayAsClosingTime && !openPastMidnight);
            // if the current time is not on the other day of opening, that means its probably tomorrow. Otherwise its the same day as opening
            // and we must add another day to get the next operating day
            const nextOperatingDay = isOtherDayFromOpeningTime
                ? momentToday.day()
                : // TODO: need to fix this hard coded based on difference of days between opening and closing
                    momentToday.add(1, "d").day();
            // we need to go through all possible days from the next operating to find the next hours
            // we dont just use the next day because the store could actually be closed and open that day after
            const days = [];
            for (let i = 0, day = nextOperatingDay; i < 7; i++) {
                day = day % 7;
                days.push(day);
                day++;
            }
            let nextOpeningHour = null;
            for (let i = 0; i < days.length; i++) {
                nextOpeningHour = openingHours.find((openingHour) => { var _a; return ((_a = openingHour.open) === null || _a === void 0 ? void 0 : _a.day) === days[i]; });
                // exit loop as soon as nextOpeningHour is found
                if (nextOpeningHour) {
                    break;
                }
            }
            // we have found the next opening hours
            if (nextOpeningHour) {
                // whether the day for current time is actually open
                // for example the store could be closed 11pm, the current time is 1130pm then the store is not open today but tomorrow. Also
                // another example could be that it is 6am and the store opens 10am the same day. Therefore the store is actually open today for
                // the current time
                const openToday = nextOperatingDay === nextOpeningHour.open.day;
                // date/ time display:
                //  - Friday
                //  - 10:30am
                const dayFormatted = moment()
                    .tz(timeZoneId)
                    .day(nextOpeningHour.open.day)
                    .format(dayFormat);
                const timeFormatted = moment()
                    .tz(timeZoneId)
                    .hour(parseInt(nextOpeningHour.open.time.substring(0, 2), 10))
                    .minute(parseInt(nextOpeningHour.open.time.substring(2, 4), 10))
                    .format(timeFormat);
                // 1. current time is on the same day as next opening = stored closed - opens 10:30am
                // 2. current time is not on the same day as next opening = stored closed - opens tomorrow 10:30am
                // 3. the stores isn't going to open the day of the current time = stored closed today - opens Monday 10:30am
                return openToday
                    ? `store closed - opens ${isOtherDayFromOpeningTime ? "" : "tomorrow"} ${timeFormatted}`
                    : `store closed today - opens ${dayFormatted} ${timeFormatted}`;
            }
            // in the edge case where its next opening hour can't be found
            // (possibly due to opening hours being mis-configured)
            // omit the day and time details
            return "store closed";
        }
    }, [contextRetailLocation, tick, canPickup, canDeliver]);
    return (0, react_1.useMemo)(() => ({
        isOperating,
        canPickup,
        canDeliver,
        retailLocationAvailableForOrdering,
        allowedOrderTimes,
        operatingStatusLabel
    }), [
        isOperating,
        canPickup,
        canDeliver,
        retailLocationAvailableForOrdering,
        allowedOrderTimes,
        operatingStatusLabel
    ]);
};
exports.useRetailLocationAvailability = useRetailLocationAvailability;
const useRetailLocationMaximumOrderValueCheck = (retailLocation) => {
    const { totalPrice, salesOrderItems } = (0, react_1.useContext)(SalesOrderContext_1.SalesOrderContext);
    const { orderType } = (0, react_1.useContext)(OrderTypeContext_1.OrderTypeContext);
    const hasFulfilledMaximumOrderValue = (0, react_1.useMemo)(() => {
        if (!retailLocation) {
            return false;
        }
        const { maximumOrderValue, deliveryCost } = retailLocation;
        if (!maximumOrderValue) {
            return true;
        }
        if (orderType === "delivery") {
            const serviceFeeItem = salesOrderItems.find(SO => SO.productId === "SERVICE");
            const serviceFee = serviceFeeItem ? serviceFeeItem.totalPrice : 0;
            return totalPrice - deliveryCost - serviceFee <= maximumOrderValue;
        }
        return totalPrice <= maximumOrderValue;
    }, [orderType, totalPrice, retailLocation]);
    const showMaximumAmountMsg = (0, react_1.useMemo)(() => !hasFulfilledMaximumOrderValue, [
        hasFulfilledMaximumOrderValue
    ]);
    return (0, react_1.useMemo)(() => ({
        hasFulfilledMaximumOrderValue,
        showMaximumAmountMsg
    }), [hasFulfilledMaximumOrderValue, showMaximumAmountMsg]);
};
exports.useRetailLocationMaximumOrderValueCheck = useRetailLocationMaximumOrderValueCheck;
const useRequestedReadyTimeLabel = (retailLocation, requestedReadyTime, dateFormat) => {
    const tick = (0, useTickReRender_1.useTickReRender)();
    const { retailLocations } = (0, useRequiredContext_1.useRequiredContext)(RetailLocationsContext_1.RetailLocationsContext);
    const contextRetailLocation = (0, react_1.useMemo)(() => {
        if (!retailLocation) {
            return null;
        }
        if (!Array.isArray(retailLocations)) {
            return null;
        }
        return retailLocations.find(rl => rl.retailLocationId === retailLocation.retailLocationId);
    }, [retailLocation, retailLocations]);
    const { isOperating } = (0, exports.useRetailLocationAvailability)(contextRetailLocation);
    // The label to show for the current scheduled order time
    return (0, react_1.useMemo)(() => {
        var _a;
        const timeZoneId = ((_a = contextRetailLocation === null || contextRetailLocation === void 0 ? void 0 : contextRetailLocation.timezone) === null || _a === void 0 ? void 0 : _a.timeZoneId) || moment.tz.guess();
        const formattedRequestedReadyTime = (0, time_1.toTime)(requestedReadyTime, timeZoneId, dateFormat || constants_1.humanTimeFormat);
        const asapLabel = `Asap`;
        const laterLabel = `Later - ${formattedRequestedReadyTime}`;
        const currentTimestamp = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        // can't place order when there is no location
        if (!contextRetailLocation) {
            return requestedReadyTime > currentTimestamp ? laterLabel : asapLabel;
        }
        // store is no longer operating (past closing) so no order can be placed!
        if (!isOperating) {
            return asapLabel;
        }
        const { openingTime } = contextRetailLocation.getOperatingDetailsForTime(currentTimestamp);
        // the current time has passed the requested ready time time that was set. this could be due to the ui staying open for a period.
        // therefore we just say that it is being ordered for opening or Now
        if (requestedReadyTime <= currentTimestamp) {
            return asapLabel;
        }
        // otherwise it's an actual time for later
        return requestedReadyTime < openingTime ? asapLabel : laterLabel;
    }, [
        isOperating,
        contextRetailLocation,
        tick,
        requestedReadyTime,
        dateFormat
    ]);
};
exports.useRequestedReadyTimeLabel = useRequestedReadyTimeLabel;
const useOrderWithinOperatingHours = (retailLocation) => {
    const tick = (0, useTickReRender_1.useTickReRender)();
    const { retailLocations } = (0, useRequiredContext_1.useRequiredContext)(RetailLocationsContext_1.RetailLocationsContext);
    const contextRetailLocation = (0, react_1.useMemo)(() => {
        if (!retailLocation) {
            return null;
        }
        if (!Array.isArray(retailLocations)) {
            return null;
        }
        return retailLocations.find(rl => rl.retailLocationId === retailLocation.retailLocationId);
    }, [retailLocation, retailLocations]);
    const { requestedReadyTime } = (0, react_1.useContext)(SalesOrderContext_1.SalesOrderContext);
    const { isOperating } = (0, exports.useRetailLocationAvailability)(contextRetailLocation);
    // check whether an order can be placed for the current time
    return (0, react_1.useMemo)(() => {
        // can't place order if there is no location
        if (!contextRetailLocation) {
            return false;
        }
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const { openingTime, closingTime, isOpenToday } = contextRetailLocation.getOperatingDetailsForTime(timestampNow);
        // can't place order if store is not open today
        if (!isOpenToday) {
            return false;
        }
        if (!isOperating) {
            return false;
        }
        // requested ready time set by customer
        if (requestedReadyTime !== null) {
            // can't place order if the time is before opening
            if (requestedReadyTime < openingTime) {
                return false;
            }
            // can't place order if the time is after opening
            if (requestedReadyTime > closingTime) {
                return false;
            }
        }
        return true;
    }, [requestedReadyTime, isOperating, contextRetailLocation, tick]);
};
exports.useOrderWithinOperatingHours = useOrderWithinOperatingHours;
const useCanPlaceOrder = (retailLocation, deliveryAddress) => {
    const tick = (0, useTickReRender_1.useTickReRender)();
    const { retailLocations } = (0, useRequiredContext_1.useRequiredContext)(RetailLocationsContext_1.RetailLocationsContext);
    const contextRetailLocation = (0, react_1.useMemo)(() => {
        if (!retailLocation) {
            return null;
        }
        if (!Array.isArray(retailLocations)) {
            return null;
        }
        return retailLocations.find(rl => rl.retailLocationId === retailLocation.retailLocationId);
    }, [retailLocation, retailLocations]);
    const { salesOrderItems, isUpdatingCart } = (0, react_1.useContext)(SalesOrderContext_1.SalesOrderContext);
    const { orderType } = (0, useRequiredContext_1.useRequiredContext)(OrderTypeContext_1.OrderTypeContext);
    const orderTimeWithinOperatingHours = (0, exports.useOrderWithinOperatingHours)(contextRetailLocation);
    const { hasFulfilledMinimumDeliveryAmount, isValidDeliveryAddress } = (0, useRetailLocationDeliveryDetails_1.useRetailLocationDeliveryDetails)(contextRetailLocation);
    const { hasFulfilledMaximumOrderValue } = (0, exports.useRetailLocationMaximumOrderValueCheck)(contextRetailLocation);
    const { canPickup, canDeliver, isOperating } = (0, exports.useRetailLocationAvailability)(contextRetailLocation);
    // check whether an order can be placed for the current time
    return (0, react_1.useMemo)(() => {
        // can't place order if there is no location
        if (!contextRetailLocation) {
            return false;
        }
        if (!isOperating) {
            return false;
        }
        if (!orderTimeWithinOperatingHours) {
            return false;
        }
        if (orderType === TSalesOrderType_1.TSalesOrderType.Delivery) {
            if (!isValidDeliveryAddress(deliveryAddress)) {
                return false;
            }
            if (!hasFulfilledMinimumDeliveryAmount) {
                return false;
            }
            if (!hasFulfilledMaximumOrderValue) {
                return false;
            }
            if (!canDeliver) {
                return false;
            }
        }
        else {
            if (!canPickup) {
                return false;
            }
        }
        if (!Array.isArray(salesOrderItems) || salesOrderItems.length === 0) {
            return false;
        }
        if (isUpdatingCart) {
            return false;
        }
        // otherwise can place order
        // this is either the requested ready time:
        //  - is within business hours
        //  - is not set (this can lapse before the min time but this is handled on checkout)
        return true;
    }, [
        deliveryAddress,
        canPickup,
        canDeliver,
        contextRetailLocation,
        orderType,
        isValidDeliveryAddress,
        hasFulfilledMinimumDeliveryAmount,
        hasFulfilledMaximumOrderValue,
        salesOrderItems,
        isUpdatingCart,
        tick,
        isOperating
    ]);
};
exports.useCanPlaceOrder = useCanPlaceOrder;
const useRetailLocationAvailableForOrderType = (retailLocation, orderType) => {
    const tick = (0, useTickReRender_1.useTickReRender)();
    const { retailLocations } = (0, useRequiredContext_1.useRequiredContext)(RetailLocationsContext_1.RetailLocationsContext);
    const contextRetailLocation = (0, react_1.useMemo)(() => {
        if (!retailLocation) {
            return null;
        }
        if (!Array.isArray(retailLocations)) {
            return null;
        }
        return retailLocations.find(rl => rl.retailLocationId === retailLocation.retailLocationId);
    }, [retailLocation, retailLocations]);
    return (0, react_1.useMemo)(() => {
        if (!contextRetailLocation) {
            return false;
        }
        return contextRetailLocation.canOrderAtRetailLocation(orderType);
    }, [contextRetailLocation, orderType, tick]);
};
exports.useRetailLocationAvailableForOrderType = useRetailLocationAvailableForOrderType;
//# sourceMappingURL=useRetailLocationAvailability.js.map